import { Suspense, type ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { KeyRound } from "lucide-react";
import { useUserMenu } from "ra-core";
import { Link } from "react-router";
import { cn } from "@/lib/utils";
import { Notification } from "@/components/admin/notification";
import { Error } from "@/components/admin/error";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";

import { useConfigurationLoader } from "../root/useConfigurationLoader";
import ChangePasswordPage from "../settings/ChangePasswordPage";
import ModuleSidebar from "./ModuleSidebar";
import { TelephonyProvider } from "../telephony/TelephonyProvider";

// Idem CRM desktop shell: a persistent left-panel module sidebar (docs/15 §3) +
// a slim top bar (collapse toggle, theme, refresh, user menu). Replaces the old
// horizontal Header tab strip. Follows the shadcn-admin-kit layout pattern
// (floating sidebar + peer-driven main width).
export const Layout = ({ children }: { children: ReactNode }) => {
  useConfigurationLoader();
  return (
    <TelephonyProvider>
    <SidebarProvider>
      <ModuleSidebar />
      <main
        className={cn(
          "ml-auto w-full max-w-full",
          "peer-data-[state=collapsed]:w-[calc(100%-var(--sidebar-width-icon)-1rem)]",
          "peer-data-[state=expanded]:w-[calc(100%-var(--sidebar-width))]",
          "sm:transition-[width] sm:duration-200 sm:ease-linear",
          "flex h-svh flex-col",
        )}
      >
        <header className="flex h-12 shrink-0 items-center gap-2 px-4 border-b">
          <SidebarTrigger />
          <div className="flex-1" />
          <ThemeModeToggle />
          <RefreshButton />
          <UserMenu>
            <ChangePasswordMenuItem />
          </UserMenu>
        </header>
        <div className="flex flex-1 flex-col p-4 overflow-auto" id="main-content">
          <ErrorBoundary FallbackComponent={Error}>
            <Suspense fallback={<Skeleton className="h-12 w-12 rounded-full" />}>
              {children}
            </Suspense>
          </ErrorBoundary>
        </div>
      </main>
      <Notification />
    </SidebarProvider>
    </TelephonyProvider>
  );
};

const ChangePasswordMenuItem = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    // globalThis.Error: the admin <Error> component (imported above) shadows the
    // global Error constructor in this module.
    throw new globalThis.Error("<ChangePasswordMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ChangePasswordPage.path} className="flex items-center gap-2">
        <KeyRound />
        Change password
      </Link>
    </DropdownMenuItem>
  );
};
